import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import {
  Loader2,
  Pin,
  ArrowUpDown,
  Rows3,
  LayoutList,
  X,
  Folder as FolderIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { NoteOverlay } from "@/components/note-overlay";
import { NoteActionsMenu } from "@/components/note-actions-menu";
import {
  useListNotes,
  useDeleteNote,
  useTogglePinNote,
  useListFolders,
  useAssignNoteFolder,
  useArchiveNote,
  useDuplicateNote,
  useMergeNotes,
  getListNotesQueryKey,
  type Note,
} from "@workspace/api-client-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { stripHtml } from "@/lib/strip-html";

export function PinnedPage() {
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const { id } = useParams<{ id?: string }>();

  const [sortOrder, setSortOrder] = React.useState<
    "newest" | "oldest" | "title-asc" | "title-desc"
  >("newest");
  const [viewMode, setViewMode] = React.useState<"list" | "compact">("list");

  const [selectedNoteId, setSelectedNoteId] = React.useState<number | null>(
    null,
  );
  const [openMoveSheet, setOpenMoveSheet] = React.useState(false);
  const [openMergeSheet, setOpenMergeSheet] = React.useState(false);
  const [mergeSourceId, setMergeSourceId] = React.useState<number | null>(
    null,
  );
  const [folderSearchQuery, setFolderSearchQuery] = React.useState("");
  const [confirmDeleteId, setConfirmDeleteId] = React.useState<number | null>(
    null,
  );

  const { data: allNotes = [], isLoading } = useListNotes();
  const { data: folders = [] } = useListFolders();
  const assignFolder = useAssignNoteFolder();
  const deleteNote = useDeleteNote();
  const archiveNote = useArchiveNote();
  const duplicateNote = useDuplicateNote();
  const mergeNotes = useMergeNotes();

  const filteredFolders = folders.filter((folder) =>
    folder.name.toLowerCase().includes(folderSearchQuery.toLowerCase()),
  );

  const folderColorPalette = [
    {
      bg: "bg-blue-100 dark:bg-blue-950",
      text: "text-blue-600 dark:text-blue-400",
    },
    {
      bg: "bg-green-100 dark:bg-green-950",
      text: "text-green-600 dark:text-green-400",
    },
    {
      bg: "bg-orange-100 dark:bg-orange-950",
      text: "text-orange-600 dark:text-orange-400",
    },
    {
      bg: "bg-purple-100 dark:bg-purple-950",
      text: "text-purple-600 dark:text-purple-400",
    },
    {
      bg: "bg-pink-100 dark:bg-pink-950",
      text: "text-pink-600 dark:text-pink-400",
    },
    {
      bg: "bg-teal-100 dark:bg-teal-950",
      text: "text-teal-600 dark:text-teal-400",
    },
  ];

  const getFolderColor = (folderId: number) =>
    folderColorPalette[folderId % folderColorPalette.length];

  const pinnedNotes = React.useMemo(
    () => allNotes.filter((n) => n.pinned),
    [allNotes],
  );

  const notes = React.useMemo(() => {
    const sorted = [...pinnedNotes];
    switch (sortOrder) {
      case "oldest":
        return sorted.sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
      case "title-asc":
        return sorted.sort((a, b) =>
          (a.title || "").localeCompare(b.title || ""),
        );
      case "title-desc":
        return sorted.sort((a, b) =>
          (b.title || "").localeCompare(a.title || ""),
        );
      default:
        return sorted.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
    }
  }, [pinnedNotes, sortOrder]);

  const togglePin = useTogglePinNote();

  const handleTogglePin = (noteId: number) => {
    togglePin.mutate(
      { id: noteId },
      {
        onSuccess: (updated) => {
          const updateList = (list: Note[] | undefined) =>
            list?.map((n) =>
              n.id === noteId ? { ...n, pinned: updated.pinned } : n,
            );
          queryClient.setQueryData<Note[]>(getListNotesQueryKey(), updateList);
        },
      },
    );
  };

  const handleMoveToFolder = (noteId: number, folderId: number | null) => {
    assignFolder.mutate(
      { id: noteId, data: { folderId: folderId ?? undefined } },
      {
        onSuccess: (updated) => {
          const patch = (list: Note[] | undefined) =>
            list?.map((n) => (n.id === updated.id ? updated : n));
          queryClient.setQueryData<Note[]>(getListNotesQueryKey(), patch);
        },
      },
    );
  };

  const handleDelete = (noteId: number) => {
    deleteNote.mutate(
      { id: noteId },
      {
        onSuccess: () => {
          const remove = (list: Note[] | undefined) =>
            list?.filter((n) => n.id !== noteId);
          queryClient.setQueryData<Note[]>(getListNotesQueryKey(), remove);
        },
      },
    );
  };

  const handleArchive = (noteId: number) => {
    archiveNote.mutate(
      { id: noteId },
      {
        onSuccess: () => {
          const remove = (list: Note[] | undefined) =>
            list?.filter((n) => n.id !== noteId);
          queryClient.setQueryData<Note[]>(getListNotesQueryKey(), remove);
        },
      },
    );
  };

  const handleDuplicate = (noteId: number) => {
    duplicateNote.mutate(
      { id: noteId },
      {
        onSuccess: (created) => {
          queryClient.setQueryData<Note[]>(getListNotesQueryKey(), (list) =>
            list ? [created, ...list] : [created],
          );
        },
      },
    );
  };

  const handleMergeNotes = (noteId: number) => {
    setMergeSourceId(noteId);
    setOpenMergeSheet(true);
  };

  const handleSelectMergeTarget = (secondNoteId: number) => {
    if (mergeSourceId === null) return;
    mergeNotes.mutate(
      { id: mergeSourceId, data: { secondNoteId } },
      {
        onSuccess: (created) => {
          queryClient.setQueryData<Note[]>(getListNotesQueryKey(), (list) => {
            const filtered = list?.filter(
              (n) => n.id !== mergeSourceId && n.id !== secondNoteId,
            );
            return filtered ? [created, ...filtered] : [created];
          });
          setOpenMergeSheet(false);
          setMergeSourceId(null);
          setLocation(`/browse/${created.id}`);
        },
      },
    );
  };

  const openNoteId = id ? Number(id) : null;
  const openNote =
    openNoteId != null ? notes.find((n) => n.id === openNoteId) : undefined;
  const handleOpen = (note: Note) => setLocation(`/pinned/${note.id}`);
  const handleCloseOverlay = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      setLocation("/pinned");
    }
  };

  return (
    <div className="max-w-4xl mx-auto h-[calc(100vh-4rem-3.25rem)] flex flex-col px-4 relative">
      <div className="pt-6 pb-4 shrink-0 flex items-center gap-3">
        <div className="h-11 w-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <Pin className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold">Pinned Notes</h2>
          <p className="text-xs text-muted-foreground">
            Smartly pinned for quick access.
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="h-9 w-9 rounded-full border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0"
              aria-label="Sort"
            >
              <ArrowUpDown className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setSortOrder("newest")}>
              Newest First
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSortOrder("oldest")}>
              Oldest First
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSortOrder("title-asc")}>
              Title (A → Z)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSortOrder("title-desc")}>
              Title (Z → A)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          type="button"
          onClick={() => setViewMode(viewMode === "list" ? "compact" : "list")}
          className="h-9 w-9 rounded-full border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0"
          aria-label="Toggle layout"
        >
          {viewMode === "list" ? (
            <Rows3 className="h-4 w-4" />
          ) : (
            <LayoutList className="h-4 w-4" />
          )}
        </button>
      </div>

      <ScrollArea className="flex-1 -mx-4 px-4 pb-6">
        <div className="flex flex-col gap-3 py-2">
          {isLoading ? (
            <div className="flex justify-center text-muted-foreground items-center gap-2 py-10">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Loading pinned notes...</span>
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center text-muted-foreground py-20">
              No pinned notes yet. Pin a note from the Browser page to see it
              here.
            </div>
          ) : (
            notes.map((note) =>
              viewMode === "compact" ? (
                <div
                  key={note.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpen(note)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleOpen(note);
                  }}
                  className="group grid grid-cols-[1fr_auto] items-center gap-2 px-3 py-2.5 border-b last:border-b-0 cursor-pointer hover:bg-muted/40 transition-colors"
                >
                  <span className="text-[15px] font-medium truncate min-w-0">
                    {note.title || stripHtml(note.content)}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <NoteActionsMenu
                      note={note}
                      onTogglePin={handleTogglePin}
                      onMoveTo={(noteId) => {
                        setSelectedNoteId(noteId);
                        setOpenMoveSheet(true);
                      }}
                      onDelete={(noteId) => setConfirmDeleteId(noteId)}
                      onArchive={handleArchive}
                      onDuplicate={handleDuplicate}
                      onMergeNotes={handleMergeNotes}
                      isPinPending={togglePin.isPending}
                      isDeletePending={deleteNote.isPending}
                      isArchivePending={archiveNote.isPending}
                    />
                  </div>
                </div>
              ) : (
                <div
                  key={note.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpen(note)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleOpen(note);
                  }}
                  className="group relative bg-card border rounded-2xl px-5 py-4 pr-12 shadow-sm cursor-pointer hover:border-primary/40 transition-colors"
                >
                  {note.title && (
                    <p className="text-[15px] font-semibold leading-relaxed mb-0.5 truncate">
                      {note.title}
                    </p>
                  )}
                  <p className="text-[15px] leading-relaxed text-muted-foreground line-clamp-2 whitespace-pre-wrap">
                    {stripHtml(note.content)}
                  </p>
                  <span className="block text-[11px] text-muted-foreground/60 font-medium mt-2">
                    {format(new Date(note.createdAt), "MMM d, yyyy · h:mm a")}
                  </span>

                  <div className="absolute right-3 top-3">
                    <NoteActionsMenu
                      note={note}
                      onTogglePin={handleTogglePin}
                      onMoveTo={(noteId) => {
                        setSelectedNoteId(noteId);
                        setOpenMoveSheet(true);
                      }}
                      onDelete={(noteId) => setConfirmDeleteId(noteId)}
                      onArchive={handleArchive}
                      onDuplicate={handleDuplicate}
                      onMergeNotes={handleMergeNotes}
                      isPinPending={togglePin.isPending}
                      isDeletePending={deleteNote.isPending}
                      isArchivePending={archiveNote.isPending}
                    />
                  </div>
                </div>
              ),
            )
          )}
        </div>
      </ScrollArea>

      {/* Move to folder */}
      <Sheet open={openMoveSheet} onOpenChange={setOpenMoveSheet}>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl flex flex-col max-h-[80vh] overflow-hidden [&>button.absolute]:hidden"
        >
          <div className="relative flex flex-col min-h-0">
            <div className="relative flex items-center justify-center py-1">
              <h2 className="text-sm font-semibold">Move to folder</h2>
              <button
                onClick={() => setOpenMoveSheet(false)}
                className="absolute right-0 h-8 w-8 rounded-full border flex items-center justify-center text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <input
              type="text"
              placeholder="Search folders..."
              value={folderSearchQuery}
              onChange={(e) => setFolderSearchQuery(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-muted border border-transparent focus:outline-none focus:border-primary text-sm mt-2"
            />
            <ScrollArea className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6 my-3">
              <div className="border rounded-lg overflow-hidden">
                <button
                  className="w-full text-left px-4 py-2.5 hover:bg-muted active:scale-[0.98] transition text-sm border-b"
                  onClick={() => {
                    if (selectedNoteId !== null) {
                      handleMoveToFolder(selectedNoteId, null);
                    }
                    setOpenMoveSheet(false);
                    setFolderSearchQuery("");
                  }}
                >
                  <div className="inline-flex items-center gap-2">
                    <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <FolderIcon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    No folder
                  </div>
                </button>

                {filteredFolders.length === 0 ? (
                  <div className="px-4 py-3 text-center text-xs text-muted-foreground border-t">
                    No folders found
                  </div>
                ) : (
                  filteredFolders.map((folder) => (
                    <button
                      key={folder.id}
                      className="w-full text-left flex items-center gap-2 px-4 py-2.5 border-b last:border-b-0 hover:bg-muted transition"
                      onClick={() => {
                        if (selectedNoteId !== null) {
                          handleMoveToFolder(selectedNoteId, folder.id);
                        }
                        setOpenMoveSheet(false);
                        setFolderSearchQuery("");
                      }}
                    >
                      <div
                        className={cn(
                          "h-9 w-9 rounded-full flex items-center justify-center shrink-0",
                          getFolderColor(folder.id).bg,
                        )}
                      >
                        <FolderIcon
                          className={cn(
                            "h-4 w-4",
                            getFolderColor(folder.id).text,
                          )}
                        />
                      </div>
                      <span className="text-sm font-medium truncate">
                        {folder.name}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </SheetContent>
      </Sheet>
      <Sheet open={openMergeSheet} onOpenChange={setOpenMergeSheet}>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl flex flex-col max-h-[80vh] overflow-hidden [&>button.absolute]:hidden"
        >
          <div className="relative flex flex-col min-h-0">
            <div className="relative flex items-center justify-center py-1">
              <h2 className="text-sm font-semibold">Merge with</h2>
              <button
                onClick={() => {
                  setOpenMergeSheet(false);
                  setMergeSourceId(null);
                }}
                className="absolute right-0 h-8 w-8 rounded-full border flex items-center justify-center text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <ScrollArea className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6 my-3">
              <div className="border rounded-lg overflow-hidden">
                {allNotes.filter((n) => n.id !== mergeSourceId).length ===
                0 ? (
                  <div className="px-4 py-3 text-center text-xs text-muted-foreground">
                    No other notes to merge with
                  </div>
                ) : (
                  allNotes
                    .filter((n) => n.id !== mergeSourceId)
                    .map((n) => (
                      <button
                        key={n.id}
                        className="w-full text-left px-4 py-2.5 hover:bg-muted active:scale-[0.98] transition text-sm border-b last:border-b-0"
                        onClick={() => handleSelectMergeTarget(n.id)}
                      >
                        <div className="font-medium truncate">
                          {n.title || "Untitled"}
                        </div>
                      </button>
                    ))
                )}
              </div>
            </ScrollArea>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete confirmation */}
      <Dialog
        open={confirmDeleteId !== null}
        onOpenChange={(open) => !open && setConfirmDeleteId(null)}
      >
        <DialogContent className="max-w-xs rounded-2xl [&>button]:hidden">
          <DialogHeader>
            <DialogTitle className="text-base">Delete note?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This note will be moved to the Trash and then deleted forever from
            all your devices after 30 days.
          </p>
          <DialogFooter className="flex-row justify-end gap-4 sm:justify-end">
            <Button
              variant="ghost"
              className="w-auto px-3"
              onClick={() => setConfirmDeleteId(null)}
            >
              Cancel
            </Button>
            <Button
              variant="ghost"
              className="w-auto px-3 text-red-500 hover:text-red-600"
              onClick={() => {
                if (confirmDeleteId !== null) handleDelete(confirmDeleteId);
                setConfirmDeleteId(null);
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {openNote && (
        <NoteOverlay
          key={openNote.id}
          note={openNote}
          onClose={handleCloseOverlay}
        />
      )}
    </div>
  );
}
